import { useState } from 'react';
import { X, Calendar, Info, Loader2 } from 'lucide-react';
import { Button } from '../../../components/Shared/Button';
import { Input } from '../../../components/Shared/Input';

interface GenerateMonthDialogProps {
  isOpen: boolean;
  onClose: () => void;
  selectedEmployeeCount: number;
  onGenerate: (month: number, year: number) => Promise<void>;
}

export function GenerateMonthDialog({ isOpen, onClose, selectedEmployeeCount, onGenerate }: GenerateMonthDialogProps) {
  const currentDate = new Date();
  const [month, setMonth] = useState(currentDate.getMonth() + 1); // 1-12
  const [year, setYear] = useState(currentDate.getFullYear());
  const [isGenerating, setIsGenerating] = useState(false);

  const handleGenerate = async () => {
    if (month < 1 || month > 12 || year < 2020 || year > 2100) {
      return;
    }

    setIsGenerating(true);
    try {
      await onGenerate(month, year);
      onClose();
    } catch (error) {
      console.error('Fehler beim Generieren:', error);
    } finally {
      setIsGenerating(false);
    }
  };

  const monthName = new Date(year, month - 1, 1).toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-xl font-semibold text-gray-900 dark:text-gray-100">
            Arbeitsmonat generieren
          </h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 transition-colors"
            disabled={isGenerating}
            aria-label="Dialog schließen"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
        {/* Info Box */}
        <div className="bg-primary-50 dark:bg-primary-900/20 border border-primary-200 dark:border-primary-800 rounded-lg p-4">
          <div className="flex gap-3">
            <Info className="w-5 h-5 text-primary-600 dark:text-primary-400 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-primary-800 dark:text-primary-200">
              <p className="font-medium mb-2">Was passiert beim Generieren?</p>
              <ul className="list-disc list-inside space-y-1 text-primary-700 dark:text-primary-300">
                <li>Für <strong>{selectedEmployeeCount} ausgewählte Mitarbeiter</strong> werden Arbeitstage erstellt</li>
                <li>Basierend auf dem jeweiligen <strong>Arbeitszeitmodell</strong></li>
                <li>Bestehende Planungseinträge werden <strong>nicht überschrieben</strong></li>
                <li>Einträge können danach mit Aufgaben befüllt werden</li>
              </ul>
            </div>
          </div>
        </div>

        {/* Month/Year Selection */}
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Monat
            </label>
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
              disabled={isGenerating}
              aria-label="Monat auswählen"
            >
              {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                <option key={m} value={m}>
                  {new Date(2025, m - 1, 1).toLocaleDateString('de-DE', { month: 'long' })}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Jahr
            </label>
            <Input
              type="number"
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              min={2020}
              max={2100}
              disabled={isGenerating}
            />
          </div>
        </div>

        {/* Preview */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4">
          <div className="flex items-center gap-2 text-gray-700 dark:text-gray-300">
            <Calendar className="w-5 h-5" />
            <span className="font-medium">Gewählter Monat:</span>
            <span className="text-primary-600 dark:text-primary-400 font-semibold">{monthName}</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <Button variant="outline" onClick={onClose} disabled={isGenerating}>
            Abbrechen
          </Button>
          <Button onClick={handleGenerate} disabled={isGenerating || selectedEmployeeCount === 0}>
            {isGenerating ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Generiere...
              </>
            ) : (
              <>
                <Calendar className="w-4 h-4" />
                Monat generieren
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
    </div>
  );
}
